// NEW_FILE_JUSTIFIED: New hook for WhatsApp personal messages — composing queries/mutations/actions from the whatsapp Convex module.
/**
 * useWhatsAppMessages Hook
 * Composes Convex queries, mutations, and actions for the WhatsApp personal
 * messages feature. Maps raw Convex data to the IConversation / IMessage
 * shapes expected by ConversationList and ChatPanel.
 */

import { useQuery, useMutation, useAction } from 'convex/react';
import { useCallback, useMemo } from 'react';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: Convex generated API lives outside src/; no @convex/ path alias in tsconfig — all hooks in src/hooks/ use this relative path
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: Convex generated types
import type { IConversation, IMessage, ISendMediaArgs } from '@/@types/messages';
import type { IUseWhatsAppMessagesReturn } from '@/@types/whatsapp';
import { getInitials, getAvatarColor, formatTime } from '@/lib/message-utils';

// ============================================================================
// Hook
// ============================================================================

export function useWhatsAppMessages(selectedContactId: string | null): IUseWhatsAppMessagesReturn {
  // Live conversation list — updates in real time via Convex subscription
  const rawConversations = useQuery(api.whatsapp.queries.getConversations, {});

  // Live messages for the selected contact — skipped when nothing is selected
  const rawMessages = useQuery(
    api.whatsapp.queries.getMessages,
    selectedContactId !== null ? { whatsappContactId: selectedContactId } : 'skip'
  );

  const markReadMutation = useMutation(api.whatsapp.mutations.markRead);
  const sendMessageAction = useAction(api.whatsapp.actions.sendMessage);
  const sendMediaMessageAction = useAction(api.whatsapp.actions.sendMediaMessage);
  const sendReactionAction = useAction(api.whatsapp.actions.sendReaction);
  const deleteMessageAction = useAction(api.whatsapp.actions.deleteMessageAction);

  // Contact metadata (phone, profile pic) — reactive subscription
  const rawContact = useQuery(
    api.whatsapp.queries.getContact,
    selectedContactId !== null ? { whatsappContactId: selectedContactId } : 'skip'
  );

  const fetchContactInfoAction = useAction(api.whatsapp.actions.fetchContactInfo);

  // Map Convex conversation rows to IConversation for ConversationList
  const conversations = useMemo((): IConversation[] => {
    if (rawConversations === undefined) return [];
    return rawConversations.map(conv => {
      const isGroup = conv.chatType === 'group';
      const lastMessage =
        isGroup && conv.lastMessageSenderName
          ? `${conv.lastMessageSenderName}: ${conv.lastMessage}`
          : conv.lastMessage;

      return {
        id: conv.whatsappContactId,
        contactName: conv.contactName,
        contactInitials: getInitials(conv.contactName),
        contactUsername: conv.contactUsername,
        avatarColor: getAvatarColor(conv.whatsappContactId),
        lastMessage,
        lastMessageTime: formatTime(conv.lastMessageTimestamp),
        unreadCount: conv.unreadCount,
        isStarred: false,
        channel: 'whatsapp',
        chatType: conv.chatType,
        lastMessageSenderName: conv.lastMessageSenderName,
        profilePicUrl: conv.profilePicUrl,
      };
    });
  }, [rawConversations]);

  // Map Convex message rows to IMessage for ChatPanel / MessageBubble
  const messages = useMemo((): IMessage[] => {
    if (rawMessages === undefined) return [];
    const contactId = selectedContactId ?? '';
    return rawMessages.map(msg => ({
      id: msg._id,
      conversationId: contactId,
      sender: msg.direction === 'incoming' ? 'lead' : 'agent',
      senderName: msg.direction === 'incoming' ? msg.contactName : 'You',
      senderInitials: msg.direction === 'incoming' ? getInitials(msg.contactName) : 'ME',
      avatarColor: msg.direction === 'incoming' ? getAvatarColor(contactId) : '#B8EF70',
      text:
        msg.mediaType &&
        /^\[(?:Image|Photo|Video|Audio|Document|Sticker|Voice message|File(?::\s.*)?)\]$/.test(
          msg.text ?? ''
        )
          ? ''
          : (msg.text ?? ''),
      timestamp: formatTime(msg.timestamp),
      isOutgoing: msg.direction === 'outgoing',
      groupSenderName: msg.chatType === 'group' ? msg.senderName : undefined,
      imageThumbnail: msg.imageThumbnail,
      mediaStorageId: msg.mediaStorageId,
      mediaType: msg.mediaType,
      mediaUrl: msg.mediaUrl,
      mediaFilename: msg.mediaFilename,
      mediaMimeType: msg.mediaMimeType,
      mediaSize: msg.mediaSize,
      reactions: msg.reactions,
      linkPreviewUrl: msg.linkPreviewUrl,
      linkPreviewTitle: msg.linkPreviewTitle,
      linkPreviewDescription: msg.linkPreviewDescription,
      linkPreviewSiteName: msg.linkPreviewSiteName,
      linkPreviewImageThumbnail: msg.linkPreviewImageThumbnail,
      replyToMessageId: msg.replyToMessageId,
      replyToText: msg.replyToText,
      replyToSenderName: msg.replyToSenderName,
      deletedForMe: msg.deletedForMe,
      deletedForEveryone: msg.deletedForEveryone,
      platformMessageId: msg.whatsappMessageId,
      isFromMe: msg.direction === 'outgoing',
    }));
  }, [rawMessages, selectedContactId]);

  const markRead = useCallback(
    async (whatsappContactId: string): Promise<void> => {
      await markReadMutation({ whatsappContactId });
    },
    [markReadMutation]
  );

  const sendMessage = useCallback(
    async (args: {
      whatsappContactId: string;
      contactName: string;
      contactUsername?: string;
      text: string;
      chatType?: 'personal' | 'group';
      replyToMessageId?: string;
    }): Promise<void> => {
      await sendMessageAction(args);
    },
    [sendMessageAction]
  );

  const sendMediaMessage = useCallback(
    async (args: ISendMediaArgs): Promise<void> => {
      await sendMediaMessageAction({
        whatsappContactId: args.contactId,
        contactName: args.contactName,
        contactUsername: args.contactUsername,
        storageId: args.storageId as Id<'_storage'>, // AS_JUSTIFIED: storageId is generated by Convex storage upload, guaranteed to be valid
        mediaType: args.mediaType,
        filename: args.filename,
        mimeType: args.mimeType,
        size: args.size,
        text: args.text,
        chatType: args.chatType,
      });
    },
    [sendMediaMessageAction]
  );

  const sendReaction = useCallback(
    async (whatsappMessageId: string, emoji: string, isFromMe: boolean): Promise<void> => {
      if (selectedContactId === null) return;
      await sendReactionAction({
        whatsappContactId: selectedContactId,
        whatsappMessageId,
        emoji,
        isFromMe,
      });
    },
    [sendReactionAction, selectedContactId]
  );

  const deleteMessage = useCallback(
    async (whatsappMessageId: string, forEveryone: boolean, isFromMe: boolean): Promise<void> => {
      if (selectedContactId === null) return;
      await deleteMessageAction({
        whatsappContactId: selectedContactId,
        whatsappMessageId,
        forEveryone,
        isFromMe,
      });
    },
    [deleteMessageAction, selectedContactId]
  );

  const contactDetails = useMemo(() => {
    if (rawContact === undefined || rawContact === null) return null;
    return {
      phone: rawContact.contactPhone,
      profilePicUrl: rawContact.profilePicUrl,
    };
  }, [rawContact]);

  const fetchContactInfo = useCallback(
    async (whatsappContactId: string): Promise<void> => {
      await fetchContactInfoAction({ whatsappContactId });
    },
    [fetchContactInfoAction]
  );

  return {
    conversations,
    messages,
    isLoading: rawConversations === undefined,
    markRead,
    sendMessage,
    sendMediaMessage,
    contactDetails,
    fetchContactInfo,
    sendReaction,
    deleteMessage,
  };
}
